/** Accessible breadcrumb navigation. Wraps React Aria Breadcrumbs. */
import {
  Breadcrumbs as AriaBreadcrumbs,
  Breadcrumb as AriaBreadcrumb,
  type BreadcrumbsProps as AriaBreadcrumbsProps,
} from 'react-aria-components';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

interface BreadcrumbItem {
  id: string;
  label: string;
  href?: string;
}

interface BreadcrumbsProps extends Omit<AriaBreadcrumbsProps<BreadcrumbItem>, 'children'> {
  items: BreadcrumbItem[];
  className?: string;
}

function Breadcrumbs({ items, className, ...props }: BreadcrumbsProps) {
  return (
    <AriaBreadcrumbs
      {...props}
      className={cn(
        'flex items-center gap-1 text-[length:var(--f-text-xs)] text-[color:var(--f-fg-muted)]',
        className
      )}
    >
      {items.map((item, idx) => (
        <AriaBreadcrumb key={item.id} id={item.id} className="flex items-center gap-1">
          {item.href ? (
            <RouterLink
              to={item.href}
              className="hover:text-[color:var(--f-fg-secondary)] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--f-accent)] rounded-[var(--f-radius-sm)]"
            >
              {item.label}
            </RouterLink>
          ) : (
            <span className="text-[color:var(--f-fg-secondary)] font-medium">{item.label}</span>
          )}
          {idx < items.length - 1 && (
            <ChevronRight size={12} className="text-[color:var(--f-fg-muted)]" />
          )}
        </AriaBreadcrumb>
      ))}
    </AriaBreadcrumbs>
  );
}

export { Breadcrumbs };
export type { BreadcrumbsProps, BreadcrumbItem };
